import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Skewed (parallelogram) CTA button matching the VISION ONE brand geometry.
 * The wrapper is skewed, the inner label is counter-skewed so text stays upright.
 */
const paraVariants = cva(
  "group relative inline-flex select-none items-center justify-center overflow-hidden font-medium tracking-wide outline-none transition-[transform,box-shadow,background-color,border-color,color] duration-300 ease-out focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98]",
  {
    variants: {
      variant: {
        primary:
          "bg-gradient-cta text-primary-foreground shadow-cta hover:-translate-y-0.5 hover:shadow-[0_22px_54px_-16px_hsl(230_95%_58%/0.9)]",
        outline:
          "border border-foreground/25 bg-foreground/[0.04] text-foreground backdrop-blur-md hover:-translate-y-0.5 hover:border-foreground/50 hover:bg-foreground/[0.09]",
        subtle:
          "border border-primary/40 bg-background/50 text-foreground backdrop-blur-md hover:border-primary/80 hover:bg-primary/10",
      },
      size: {
        sm: "h-10 px-6 text-sm",
        md: "h-12 px-8 text-sm sm:text-base",
        lg: "h-14 px-10 text-base",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  }
);

export const ParallelogramButton = React.forwardRef(
  ({ className, variant, size, children, skew = 14, ...props }, ref) => (
    <button
      ref={ref}
      style={{ transform: `skewX(-${skew}deg)` }}
      className={cn(paraVariants({ variant, size }), className)}
      {...props}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-foreground/20 opacity-0 blur-md transition-opacity duration-300 group-hover:animate-sheen group-hover:opacity-100"
      />
      <span
        className="relative flex items-center gap-2 whitespace-nowrap"
        style={{ transform: `skewX(${skew}deg)` }}
      >
        {children}
      </span>
    </button>
  )
);
ParallelogramButton.displayName = "ParallelogramButton";
